// @givmo/app-charity — one back-office setup record (CBO-IN3, surface 2).
//
// ══ WHAT THIS SCREEN IS ════════════════════════════════════════════════════════════════════════
//
// The record itself: the details the organization recorded about how it is registered, where the
// record stands, and its event log. While the record is a draft this is also the form; afterwards it
// is the account of what happened.
//
// ⚠ THIS IS NOT A PROGRESS BAR, AND THE DIFFERENCE IS THE POINT. There is no step counter, no
// percentage and no "you're almost there", because the thing being tracked is not the person's
// progress through a form — it is a lifecycle the backend owns, whose transitions happen when their
// preconditions are satisfied and not before. Every readiness word on this page derives from the
// status the wire returned.
//
// ⚠ AND IT DOES NOT PROMISE COMPLETENESS. "Finish setup" finishes SETUP — the organization's entity
// and books get created. It is not a claim that the books hold everything, and no state on this page
// treats a finished setup as one; bringing history in is an open-ended surface afterwards, with its
// own records. That distinction is the whole reason the submit hint says what it says.
//
// ══ THE GATES, IN ORDER ════════════════════════════════════════════════════════════════════════
//
//   1. ORG — fail closed on `isReady`, exactly as the landing does. No read fires against an empty
//      charity id, and a mid-session org switch remounts this page (the org-scoped boundary keys on
//      the active charity), so the form never has to defend against the org changing under it.
//   2. MODULE — a 404 is "not available here", never an error. ⚠ And never "the module is off"
//      either: the gate and a record outside the caller's scope answer byte-identically, so a stale
//      bookmark or an org switch mid-page lands here with the module fully ON. The copy names both
//      possibilities without choosing between them, which is the only true sentence available.
//   3. ROLE — every mutating control is Owner-only and renders only once the caller is POSITIVELY
//      confirmed an Owner of the ACTIVE org. A pending or errored membership read gets the read-only
//      view. The backend scopes the same writes to Owners; the UI never offers what it would refuse.
//   4. STATE MACHINE — Save renders only while the record is editable, and Withdraw only while the
//      state machine still allows it. An approved record cannot withdraw (it carries a permanent
//      ledger identity a committed store row may already use), so the affordance is absent rather
//      than present-and-refused.
//
// The root keeps `data-testid="page-enrollment-record"`.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  ErrorMessage,
  Icon,
  PageHeader,
  Select,
  Skeleton,
  TextField,
  testIdProp,
} from '@givmo/design-system';
import { Building2, ShieldAlert } from 'lucide-react';
import { isOwnerRole } from '../admins/admins-roles';
import { useActiveOrg } from '../../org/active-org-context';
import {
  canWithdraw,
  displayEnrollmentDate,
  isEditable,
  isModuleUnavailable,
  isSetUp,
  readEnrollmentDetail,
  readMonth,
  serverMessage,
  type EnrollmentDetail,
} from './enrollment-decode';
import {
  getEnrollment,
  submitEnrollment,
  updateEnrollmentDraft,
  withdrawEnrollment,
  type EnrollmentDraftUpdateBody,
} from './enrollment-transport';
import {
  DEFAULT_FISCAL_YEAR_END_MONTH,
  ENROLLMENT_COPY,
  FISCAL_YEAR_MONTHS,
  enrollmentActionLabel,
  enrollmentStatusVocab,
  fiscalYearMonthLabel,
} from './enrollment-vocab';
import { ENROLLMENT_ROUTES } from './enrollment-routes';
import { enrollmentListKey } from './enrollment-page';

const PREFIX = 'enrollment-record';

/** One record's cache key. Hand-written; no generated family predicate matches it. */
export function enrollmentRecordKey(
  charityId: string | null,
  enrollmentId: string,
): readonly unknown[] {
  return ['enrollment', 'record', charityId, enrollmentId];
}

export interface EnrollmentRecordPageProps {
  enrollmentId: string;
}

export function EnrollmentRecordPage({ enrollmentId }: EnrollmentRecordPageProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { activeMembership, activeCharityId, isReady, isPending, isError, refetch } =
    useActiveOrg();

  const isOwner = activeMembership !== null && isOwnerRole(activeMembership.role);

  const recordQuery = useQuery({
    queryKey: enrollmentRecordKey(activeCharityId, enrollmentId),
    queryFn: () => getEnrollment(activeCharityId ?? '', enrollmentId),
    select: readEnrollmentDetail,
    enabled: isReady && activeCharityId !== null,
  });

  /** Every write answers with the record, so each one seeds both caches instead of refetching. */
  function absorb(body: unknown) {
    queryClient.setQueryData(enrollmentRecordKey(activeCharityId, enrollmentId), body);
    void queryClient.invalidateQueries({ queryKey: enrollmentListKey(activeCharityId) });
  }

  const saveMutation = useMutation({
    mutationFn: (body: EnrollmentDraftUpdateBody) =>
      updateEnrollmentDraft(activeCharityId ?? '', enrollmentId, body),
    onSuccess: absorb,
  });

  const submitMutation = useMutation({
    mutationFn: () => submitEnrollment(activeCharityId ?? '', enrollmentId),
    onSuccess: absorb,
    // ⚠ REFETCH ON FAILURE, because a failed submit does NOT mean an unsubmitted record. An
    // infrastructure error can land after the app already applied the transition, and submission is
    // state-based idempotent, so the only way to know what the record actually says is to ask. The
    // alternative — leaving the stale card up beside an error — is how a page ends up showing
    // "Draft" for a record that is already submitted.
    onError: () => void recordQuery.refetch(),
  });

  const withdrawMutation = useMutation({
    mutationFn: () => withdrawEnrollment(activeCharityId ?? '', enrollmentId),
    onSuccess: absorb,
  });

  // ── Org gate — fail closed ───────────────────────────────────────────────────────────────────────
  if (isPending) {
    return (
      <PageShell status={null}>
        <Card className="p-24" testId={`${PREFIX}-loading`}>
          <div className="flex flex-col gap-10">
            <Skeleton className="h-16 w-1/3" />
            <Skeleton className="h-12 w-2/3" />
          </div>
        </Card>
      </PageShell>
    );
  }

  if (isError || !isReady) {
    return (
      <PageShell status={null}>
        <Retry
          testId={`${PREFIX}-org-error`}
          title="Couldn’t load your organization"
          description="We couldn’t load your organization. Please try again."
          onRetry={refetch}
        />
      </PageShell>
    );
  }

  // ── Module gate ──────────────────────────────────────────────────────────────────────────────────
  if (recordQuery.isError && isModuleUnavailable(recordQuery.error)) {
    return (
      <PageShell status={null}>
        <div className="rounded-xl border border-border-subtle bg-surface">
          <EmptyState
            testId={`${PREFIX}-unavailable`}
            icon={<Icon icon={Building2} size={20} aria-hidden />}
            title={ENROLLMENT_COPY.recordUnavailableTitle}
            description={ENROLLMENT_COPY.recordUnavailableBody}
          />
        </div>
      </PageShell>
    );
  }

  if (recordQuery.isPending) {
    return (
      <PageShell status={null}>
        <Card className="p-24" testId={`${PREFIX}-record-loading`}>
          <Skeleton className="h-16 w-1/2" />
        </Card>
      </PageShell>
    );
  }

  if (recordQuery.isError) {
    return (
      <PageShell status={null}>
        <Retry
          testId={`${PREFIX}-error`}
          title="Couldn’t load this record"
          description={serverMessage(
            recordQuery.error,
            'We couldn’t load this record. Please try again.',
          )}
          onRetry={() => void recordQuery.refetch()}
        />
      </PageShell>
    );
  }

  const record = recordQuery.data;
  if (record === null) {
    // The response arrived but carried nothing this build could address. Never rendered as an empty
    // record — a blank form would invite edits that go nowhere.
    return (
      <PageShell status={null}>
        <div className="rounded-xl border border-border-subtle bg-surface">
          <EmptyState
            testId={`${PREFIX}-unreadable`}
            icon={<Icon icon={ShieldAlert} size={20} aria-hidden />}
            title="Couldn’t read this record"
            description="This record arrived in a shape this version of the console can’t read. Please try again later."
          />
        </div>
      </PageShell>
    );
  }

  const vocab = enrollmentStatusVocab(record.status);
  const editable = isEditable(record);

  return (
    <PageShell status={record.status === null ? null : vocab.label}>
      <Card testId={`${PREFIX}-status`}>
        <CardBody>
          <div className="flex flex-col gap-12">
            <div className="flex flex-wrap items-center gap-8">
              <Badge tone={vocab.tone} testId={`${PREFIX}-status-badge`}>
                {vocab.label}
              </Badge>
              <RecordDate label="Started" value={record.createdAt} testId={`${PREFIX}-started`} />
              <RecordDate
                label="Submitted"
                value={record.submittedAt}
                testId={`${PREFIX}-submitted`}
              />
              <RecordDate
                label="Approved"
                value={record.approvedAt}
                testId={`${PREFIX}-approved`}
              />
              <RecordDate
                label="Books created"
                value={record.provisionedAt}
                testId={`${PREFIX}-provisioned`}
              />
            </div>
            <p className="text-s14 text-neutral-700">{vocab.description}</p>
            {/* The recorded rejection or withdrawal reason, rendered verbatim. Paraphrasing it would
                put this console's words where the record holds someone else's. */}
            {record.dispositionNote !== null && (
              <p
                className="rounded-lg bg-neutral-50 px-12 py-10 text-s13 text-neutral-700"
                {...testIdProp(`${PREFIX}-disposition`)}
              >
                {record.dispositionNote}
              </p>
            )}
          </div>
        </CardBody>
      </Card>

      {editable && isOwner ? (
        <DetailsForm
          record={record}
          onSave={(body) => saveMutation.mutate(body)}
          isSaving={saveMutation.isPending}
          saveError={saveMutation.isError ? saveMutation.error : null}
          isSaved={saveMutation.isSuccess}
        />
      ) : (
        <RecordedDetails record={record} />
      )}

      {/* ⚠ THE WAY INTO THE DOORS, AND ONLY ONCE THE BOOKS EXIST. The backend renders inputs there
          for a `provisioned` record and nothing earlier, so offering the link before then would
          send somebody to a signpost. It is shown to Members too — the doors' reads need only
          `charity:read`, and a Member who can see the record can see what has been brought into it.

          It is a LINK, not a next step: bringing history in is open-ended and has no end state, so
          nothing here counts it, sequences it, or describes it as remaining work. */}
      {isSetUp(record) && (
        <Card testId={`${PREFIX}-backfill`}>
          <CardBody>
            <div className="flex flex-col gap-12">
              <p className="text-s13 text-neutral-700">{ENROLLMENT_COPY.backfillBody}</p>
              <div>
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  onClick={() => void navigate({ to: ENROLLMENT_ROUTES.backfill(record.id) })}
                  testId={`${PREFIX}-backfill-open`}
                >
                  {ENROLLMENT_COPY.backfillAction}
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {isOwner && (editable || canWithdraw(record)) && (
        <Card testId={`${PREFIX}-actions`}>
          <CardBody>
            <div className="flex flex-col gap-12">
              {editable && (
                <>
                  <p className="text-s13 text-neutral-700">{ENROLLMENT_COPY.submitHint}</p>
                  <div>
                    <Button
                      appearance="saas"
                      tone="primary"
                      size="md"
                      onClick={() => submitMutation.mutate()}
                      disabled={submitMutation.isPending}
                      testId={`${PREFIX}-submit`}
                    >
                      {ENROLLMENT_COPY.submitAction}
                    </Button>
                  </div>
                  {/* ⚠ ONE branch, no special-casing. There was a 503 branch here saying the
                      request was "recorded and nothing has been lost" — a claim the client cannot
                      make: the backend never 503s a charity submission (it swallows the refusal
                      family and answers 200 with the truthful status), so the only 503 that reaches
                      this code is an infrastructure one, where the request may never have arrived.
                      Retry framing is honest for every failure here, and safe, because a replay
                      returns the current state rather than acting twice. */}
                  {submitMutation.isError && (
                    <ErrorMessage testId={`${PREFIX}-submit-error`}>
                      {serverMessage(
                        submitMutation.error,
                        'We couldn’t submit this record. Please try again.',
                      )}
                    </ErrorMessage>
                  )}
                </>
              )}
              {canWithdraw(record) && (
                <div className="flex flex-col gap-8">
                  <div>
                    <Button
                      appearance="saas"
                      tone="ghost"
                      size="sm"
                      onClick={() => withdrawMutation.mutate()}
                      disabled={withdrawMutation.isPending}
                      testId={`${PREFIX}-withdraw`}
                    >
                      {ENROLLMENT_COPY.withdrawAction}
                    </Button>
                  </div>
                  {withdrawMutation.isError && (
                    <ErrorMessage testId={`${PREFIX}-withdraw-error`}>
                      {serverMessage(
                        withdrawMutation.error,
                        'We couldn’t withdraw this record. Please try again.',
                      )}
                    </ErrorMessage>
                  )}
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {/* ⚠ An EMPTY log renders nothing at all — never "no activity recorded". Every real record
          carries at least its own creation event, so an empty array means this client could not read
          the log, and a "nothing happened" line would state as a fact about the organization
          something that is only a fact about the decode. */}
      {record.events.length > 0 && (
        <Card testId={`${PREFIX}-log`}>
          <CardBody>
            <div className="flex flex-col gap-12">
              <h2 className="font-sans text-s14 font-w600 text-neutral-900">
                {ENROLLMENT_COPY.logTitle}
              </h2>
              <ul className="flex flex-col gap-8">
                {record.events.map((event) => {
                  const when = displayEnrollmentDate(event.createdAt);
                  return (
                    <li
                      key={event.id}
                      className="flex flex-wrap items-baseline justify-between gap-8 border-b border-border-subtle pb-8 last:border-0 last:pb-0"
                      {...testIdProp(`${PREFIX}-log-${event.id}`)}
                    >
                      <span className="text-s13 text-neutral-900">
                        {enrollmentActionLabel(event.action)}
                      </span>
                      {when.length > 0 && <span className="text-s12 text-neutral-600">{when}</span>}
                    </li>
                  );
                })}
              </ul>
            </div>
          </CardBody>
        </Card>
      )}
    </PageShell>
  );
}

/**
 * The draft form.
 *
 * ⚠ IT SENDS ONLY WHAT CHANGED. The PATCH is absent-vs-null sensitive: omitting a key preserves the
 * stored value and sending `null` clears it, so serializing the whole form on every save would
 * overwrite fields this screen never showed. A blank field the record also has blank sends nothing;
 * a field cleared BY the person sends `null`, which is the clearing they asked for.
 */
function DetailsForm({
  record,
  onSave,
  isSaving,
  saveError,
  isSaved,
}: {
  record: EnrollmentDetail;
  onSave: (body: EnrollmentDraftUpdateBody) => void;
  isSaving: boolean;
  saveError: unknown;
  isSaved: boolean;
}) {
  const [name, setName] = useState(record.legalEntityName ?? '');
  const [ein, setEin] = useState(record.legalEntityEin ?? '');
  const [jurisdiction, setJurisdiction] = useState(record.legalEntityJurisdiction ?? '');
  // ⚠ SEEDED TO DECEMBER WHEN THE RECORD HOLDS NOTHING, and that preselection is honest ONLY
  // because the hint says so in words: December is what the record settles to at submission if
  // nobody chooses, so the control shows the outcome that is actually coming rather than an empty
  // field that hides it. It stays a display default — the delta below sends nothing while it agrees
  // with what is stored, so a person who never touches it leaves the record carrying no convention
  // anybody chose, which is exactly what the backend wants of a draft.
  const [fiscalMonth, setFiscalMonth] = useState(
    record.fiscalYearEndMonth ?? DEFAULT_FISCAL_YEAR_END_MONTH,
  );

  // Re-seed when the record changes underneath (a save's response, or a refetch). Keyed on the
  // stored values rather than on the record object, so a re-render with identical data does not
  // discard something half-typed.
  useEffect(() => {
    setName(record.legalEntityName ?? '');
    setEin(record.legalEntityEin ?? '');
    setJurisdiction(record.legalEntityJurisdiction ?? '');
    setFiscalMonth(record.fiscalYearEndMonth ?? DEFAULT_FISCAL_YEAR_END_MONTH);
  }, [
    record.legalEntityName,
    record.legalEntityEin,
    record.legalEntityJurisdiction,
    record.fiscalYearEndMonth,
  ]);

  /** `undefined` = unchanged (omit the key), `null` = cleared, a string = the new value. */
  function delta(next: string, stored: string | null): string | null | undefined {
    const trimmed = next.trim();
    const value = trimmed.length > 0 ? trimmed : null;
    return value === stored ? undefined : value;
  }

  const body: EnrollmentDraftUpdateBody = {};
  const nameDelta = delta(name, record.legalEntityName);
  const einDelta = delta(ein, record.legalEntityEin);
  const jurisdictionDelta = delta(jurisdiction, record.legalEntityJurisdiction);
  if (nameDelta !== undefined) body.legal_entity_name = nameDelta;
  if (einDelta !== undefined) body.legal_entity_ein = einDelta;
  if (jurisdictionDelta !== undefined) body.legal_entity_jurisdiction = jurisdictionDelta;
  // ⚠ THE MONTH IS COMPARED AGAINST WHAT THE CONTROL WAS SEEDED WITH, NOT AGAINST THE RECORD — and
  // the difference is the whole reason the preselection is safe. Comparing against the record sends
  // `12` on every save of an unchosen draft, so somebody who only fixed a typo in the EIN would have
  // silently recorded a fiscal-year convention nobody picked, on the one field that cannot be
  // changed after the books exist. (The record-page suite caught exactly that.) Against the seed,
  // an untouched control is unchanged, and December still applies at submission — same books,
  // without the record claiming anybody chose them.
  //
  // The control has no empty option, so the CLEARING shape (`null`) is one this form never sends.
  // Un-choosing is not something a person can mean here: picking December back is what that looks
  // like, and it correctly sends nothing.
  const fiscalSeed = record.fiscalYearEndMonth ?? DEFAULT_FISCAL_YEAR_END_MONTH;
  if (fiscalMonth !== fiscalSeed) body.fiscal_year_end_month = fiscalMonth;
  // Sending no editable key is a 400 rather than a no-op, so the control is disabled until
  // something actually differs from what the record holds.
  const hasChanges = Object.keys(body).length > 0;

  return (
    <Card testId={`${PREFIX}-form`}>
      <CardBody>
        <div className="flex flex-col gap-16">
          <div className="flex flex-col gap-8">
            <h2 className="font-sans text-s16 font-w600 text-neutral-900">
              {ENROLLMENT_COPY.detailsTitle}
            </h2>
            <p className="text-s13 text-neutral-700">{ENROLLMENT_COPY.detailsBody}</p>
          </div>

          <TextField
            label={ENROLLMENT_COPY.legalNameLabel}
            autoComplete="off"
            maxLength={200}
            value={name}
            onChange={(event) => setName(event.target.value)}
            testId={`${PREFIX}-legal-name`}
          />
          <p className="-mt-8 text-s12 text-neutral-600">{ENROLLMENT_COPY.legalNameHint}</p>

          <TextField
            label={ENROLLMENT_COPY.einLabel}
            autoComplete="off"
            maxLength={32}
            value={ein}
            onChange={(event) => setEin(event.target.value)}
            testId={`${PREFIX}-ein`}
          />

          <TextField
            label={ENROLLMENT_COPY.jurisdictionLabel}
            autoComplete="off"
            maxLength={64}
            value={jurisdiction}
            onChange={(event) => setJurisdiction(event.target.value)}
            testId={`${PREFIX}-jurisdiction`}
          />

          <Select
            appearance="saas"
            label={ENROLLMENT_COPY.fiscalYearLabel}
            options={FISCAL_YEAR_MONTHS.map((month) => ({
              value: String(month.value),
              label: month.label,
            }))}
            value={String(fiscalMonth)}
            // A value this control never offered cannot be read as a month, so it is ignored rather
            // than coerced — the stored convention stands until somebody picks one that exists.
            onValueChange={(next) => {
              const month = readMonth(Number(next));
              if (month !== null) setFiscalMonth(month);
            }}
            testId={`${PREFIX}-fiscal-year`}
          />
          <p className="-mt-8 text-s12 text-neutral-600">{ENROLLMENT_COPY.fiscalYearHint}</p>

          <div className="flex flex-wrap items-center gap-12">
            <Button
              appearance="saas"
              tone="secondary"
              size="md"
              onClick={() => onSave(body)}
              disabled={!hasChanges || isSaving}
              testId={`${PREFIX}-save`}
            >
              {ENROLLMENT_COPY.saveAction}
            </Button>
            {isSaved && !hasChanges && (
              <span className="text-s12 text-neutral-600" {...testIdProp(`${PREFIX}-saved`)}>
                Saved
              </span>
            )}
          </div>
          {saveError !== null && (
            <ErrorMessage testId={`${PREFIX}-save-error`}>
              {serverMessage(saveError, 'We couldn’t save these details. Please try again.')}
            </ErrorMessage>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

/** The read-only view: what the record holds, for a Member or a record past its editable window. */
function RecordedDetails({ record }: { record: EnrollmentDetail }) {
  const rows: { label: string; value: string | null; testId: string }[] = [
    { label: ENROLLMENT_COPY.legalNameLabel, value: record.legalEntityName, testId: 'legal-name' },
    { label: ENROLLMENT_COPY.einLabel, value: record.legalEntityEin, testId: 'ein' },
    {
      label: ENROLLMENT_COPY.jurisdictionLabel,
      value: record.legalEntityJurisdiction,
      testId: 'jurisdiction',
    },
    {
      // ⚠ RENDERS ONLY WHAT THE RECORD HOLDS. A record with no convention chosen shows no row —
      // never "December" — because the read-only view is the account of what was recorded, and
      // December is what a submission WILL settle to rather than something anybody stated. The
      // form's preselection is the opposite case and says so in words; this one has no words.
      label: ENROLLMENT_COPY.fiscalYearLabel,
      value: fiscalYearMonthLabel(record.fiscalYearEndMonth) || null,
      testId: 'fiscal-year',
    },
  ].filter((row) => row.value !== null);

  if (rows.length === 0) return null;

  return (
    <Card testId={`${PREFIX}-recorded`}>
      <CardBody>
        <div className="flex flex-col gap-12">
          <h2 className="font-sans text-s16 font-w600 text-neutral-900">
            {ENROLLMENT_COPY.detailsTitle}
          </h2>
          <dl className="flex flex-col gap-8">
            {rows.map((row) => (
              <div key={row.testId} className="flex flex-wrap justify-between gap-8">
                <dt className="text-s13 text-neutral-600">{row.label}</dt>
                <dd
                  className="text-s13 text-neutral-900"
                  {...testIdProp(`${PREFIX}-recorded-${row.testId}`)}
                >
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </CardBody>
    </Card>
  );
}

/** A dated milestone. Renders NOTHING when the date is absent or unparseable. */
function RecordDate({
  label,
  value,
  testId,
}: {
  label: string;
  value: string | null;
  testId: string;
}) {
  const rendered = displayEnrollmentDate(value);
  if (rendered.length === 0) return null;
  return (
    <span className="text-s12 text-neutral-600" {...testIdProp(testId)}>
      {label} {rendered}
    </span>
  );
}

function Retry({
  testId,
  title,
  description,
  onRetry,
}: {
  testId: string;
  title: string;
  description: string;
  onRetry: () => void;
}) {
  return (
    <div className="rounded-xl border border-border-subtle bg-surface">
      <EmptyState
        testId={testId}
        icon={<Icon icon={ShieldAlert} size={20} aria-hidden />}
        title={title}
        description={description}
        action={
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={onRetry}
            testId={`${testId}-retry`}
          >
            Try again
          </Button>
        }
      />
    </div>
  );
}

function PageShell({ status, children }: { status: string | null; children: React.ReactNode }) {
  return (
    <div
      className="mx-auto flex w-full max-w-[840px] flex-col gap-24 px-24 py-24"
      {...testIdProp('page-enrollment-record')}
    >
      <PageHeader
        title={ENROLLMENT_COPY.pageTitle}
        subtitle={status ?? undefined}
        breadcrumbs={[{ label: ENROLLMENT_COPY.pageTitle, href: ENROLLMENT_ROUTES.home }]}
      />
      {children}
    </div>
  );
}
